import React from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Resuelve la URL absoluta para recursos multimedia cargados dinámicamente.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL completa formateada.
 */
const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/')
        .replace('/api/', '/')
        .replace('/api', '/');
    const cleanBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    return `${cleanBase}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * COMPONENTE: PLAYLIST OVERLAY (DESIGN / CHILD VISUAL)
 * ------------------------------------------------------------------
 * Este componente maneja la visualización del Overlay broadcast para
 * la dinámica "Arma la Playlist".
 * 
 * Renderiza el cintillo gráfico (1536px / 1920px) y los códigos QR
 * SVG con coordenadas exactas calibradas para:
 * - 2 QRs: Modo Convivencia en la Zona (quizzb9zone)
 * - 1 QR: Modo Rooster (rooster)
 * ------------------------------------------------------------------
 * 
 * @component
 * @param {Object} props
 * @param {string} props.flowState - Estado del flujo ('idle' | 'entering' | 'visible' | 'fadingOut').
 * @param {number} props.offsetY - Desplazamiento vertical en píxeles.
 * @param {string} [props.bannerImageMdUrl] - URL de imagen de fondo 1536x180 px.
 * @param {string} [props.bannerImageLgUrl] - URL de imagen de fondo 1920x180 px.
 * @param {number} [props.qrCount=1] - Cantidad de QRs (1 o 2).
 * @param {string} [props.qrCenterLogoUrl] - URL del logotipo central en QR.
 * @param {string} [props.roomId=''] - Identificador opcional de sala.
 */
export function Playlist({
    flowState = 'idle',
    offsetY = 0,
    bannerImageMdUrl = '',
    bannerImageLgUrl = '',
    qrCount = 1,
    qrCenterLogoUrl = '',
    roomId = ''
}) {
    // ZERO-CANVAS RULE: No renderizar nada si está en estado 'idle'
    if (flowState === 'idle') return null;

    const qrValue = typeof window !== 'undefined'
        ? `${window.location.origin}/playlist?s=obs${roomId ? '&roomId=' + encodeURIComponent(roomId) : ''}`
        : '';

    const mdSrc = bannerImageMdUrl
        ? getFullAssetUrl(bannerImageMdUrl)
        : '/img/overlays/rubaquiz/cintillo-ruba-1536-x-180-px.png';

    const lgSrc = bannerImageLgUrl
        ? getFullAssetUrl(bannerImageLgUrl)
        : '/img/overlays/rubaquiz/cintillo-ruba-1920-x-180-px.png';

    const centerLogo = qrCenterLogoUrl
        ? getFullAssetUrl(qrCenterLogoUrl)
        : '/img/overlays/rubaquiz/interaktia-transparent.png';

    const isDoubleQr = Number(qrCount) === 2;

    const qrImageSettings = {
        src: centerLogo,
        x: undefined,
        y: undefined,
        height: 52,
        width: 52,
        excavate: true
    };

    return (
        <div className="playlist-overlay" style={{ bottom: `${offsetY}px` }}>
            <div className={`playlist-overlay__banner playlist-overlay__banner--${flowState}`}>
                {/* 1536px and 1920px images */}
                <img
                    src={mdSrc}
                    className="playlist-overlay__banner__Image--Md"
                    alt="Arma la Playlist"
                />
                <img
                    src={lgSrc}
                    className="playlist-overlay__banner__Image--Lg"
                    alt="Arma la Playlist Lg"
                />

                {isDoubleQr ? (
                    <>
                        {/* Left QR Code (Convivencia en la zona) */}
                        <div className="playlist-overlay__banner__QR-Overlay playlist-overlay__banner__QR-Overlay--left">
                            <QRCodeSVG
                                value={qrValue}
                                size={256}
                                style={{ width: '92%', height: '92%', display: 'block' }}
                                bgColor="#d9d9d9"
                                fgColor="#000000"
                                level="H"
                                imageSettings={qrImageSettings}
                            />
                        </div>

                        {/* Right QR Code (Convivencia en la zona) */}
                        <div className="playlist-overlay__banner__QR-Overlay playlist-overlay__banner__QR-Overlay--right">
                            <QRCodeSVG
                                value={qrValue}
                                size={256}
                                style={{ width: '92%', height: '92%', display: 'block' }}
                                bgColor="#d9d9d9"
                                fgColor="#000000"
                                level="H"
                                imageSettings={qrImageSettings}
                            />
                        </div>
                    </>
                ) : (
                    /* Single QR Code (Rooster) */
                    <div className="playlist-overlay__banner__QR-Overlay playlist-overlay__banner__QR-Overlay--single">
                        <QRCodeSVG
                            value={qrValue}
                            size={256}
                            style={{ width: '92%', height: '92%', display: 'block' }}
                            bgColor="#d9d9d9"
                            fgColor="#000000"
                            level="H"
                            imageSettings={qrImageSettings}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

Playlist.propTypes = {
    flowState: PropTypes.oneOf(['idle', 'entering', 'visible', 'fadingOut']).isRequired,
    offsetY: PropTypes.number,
    bannerImageMdUrl: PropTypes.string,
    bannerImageLgUrl: PropTypes.string,
    qrCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    qrCenterLogoUrl: PropTypes.string,
    roomId: PropTypes.string
};

export default Playlist;
